import React, { useState } from 'react';
import { portfolioData } from '../data/mock';
import { Mail, MapPin, Linkedin, Send } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const ContactSection = () => {
  const { personal } = portfolioData;
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      toast({
        title: "Message Sent!",
        description: "Thank you for reaching out. I'll get back to you soon.",
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again later.",
        variant: "destructive",
      });
    }
  };

  return (
    <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0f1419]">
      <div className="max-w-5xl mx-auto">
        <div className="mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">
            <span className="text-emerald-400 font-mono text-xl">06.</span> Get In Touch
          </h2>
          <div className="h-px bg-gray-700 mt-4"></div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="md:col-span-1 space-y-4">
            <Card className="bg-[#1a1d29] border-gray-800 p-6">
              <h3 className="text-white font-semibold mb-4">Contact Information</h3>
              <div className="space-y-4">
                <a
                  href={`mailto:${personal.email}`}
                  className="flex items-start gap-3 text-gray-400 hover:text-emerald-400 transition-colors group"
                >
                  <Mail size={20} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm break-all">{personal.email}</span>
                </a>
                <div className="flex items-start gap-3 text-gray-400">
                  <MapPin size={20} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{personal.location}</span>
                </div>
                <a
                  href={personal.linkedIn}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-gray-400 hover:text-emerald-400 transition-colors group"
                >
                  <Linkedin size={20} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">LinkedIn Profile</span>
                </a>
              </div>
            </Card>

            <Card className="bg-[#1a1d29] border-gray-800 p-6">
              <p className="text-gray-400 text-sm leading-relaxed">
                I'm always interested in hearing about new opportunities, collaborations, or just 
                connecting with fellow engineers. Feel free to reach out!
              </p>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="md:col-span-2">
            <Card className="bg-[#1a1d29] border-gray-800 p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="text-sm font-medium text-gray-300 mb-2 block">
                      Name
                    </label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-[#0f1419] border-gray-700 text-white focus:border-emerald-500 focus:ring-emerald-500"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="text-sm font-medium text-gray-300 mb-2 block">
                      Email
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-[#0f1419] border-gray-700 text-white focus:border-emerald-500 focus:ring-emerald-500"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="text-sm font-medium text-gray-300 mb-2 block">
                    Subject
                  </label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="bg-[#0f1419] border-gray-700 text-white focus:border-emerald-500 focus:ring-emerald-500"
                    placeholder="What's this about?"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="text-sm font-medium text-gray-300 mb-2 block">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="bg-[#0f1419] border-gray-700 text-white focus:border-emerald-500 focus:ring-emerald-500 resize-none"
                    placeholder="Your message..."
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/50"
                >
                  Send Message
                  <Send className="ml-2" size={18} />
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
